import React from 'react';
import { TextInput, PhoneInput, Textarea, Button, Alert, Select } from '@parsina/framework';
import { C, CT } from '../../constants/theme';
import { DENTAL_SPECIALTY } from '../../constants/specialties.js';
import { useTranslation } from '../../i18n/I18nProvider.jsx';
import { bffMutation, errorCode } from '../../lib/bffClient.js';
import { DEFAULT_CURRENCY } from '../../lib/money.js';
import { currencyOptions, ownClinicInput, ownClinicFailure } from './Onboarding.shared.js';
import { JOIN_CLINIC } from '../../hooks/useProfile.js';
import { getToken, parseJwt } from '../../lib/auth.js';
import logo from 'url:../../assets/logo.png';

// Onboarding — mounted OUTSIDE the router, over the whole viewport.
//
// It exists because of one server behaviour: doctorpreference's
// GetDoctorProfile never returns NOT_FOUND — it CREATES a blank row on first
// read. So a brand-new account has an empty specialty, and "is onboarded" is
// `!!medicalCode`, not "the row exists". The BFF's gate leaves onboarding
// reachable for exactly that reason, and this is the screen that uses the gap.
//
// The specialty is SENT, not chosen. This panel is the dental module: a picker
// here would let a dentist select their way out of the app they just opened.
// The string is the 10-codepoint 'دندانپزشکی' from constants/specialties.js —
// NOT the ZWNJ-bearing clinic-type spelling, which renders identically and is
// rejected by the server-side predicate.

/**
 * The dentist's name as the token knows it, or '' for an account created by the
 * phone + SMS-code flow — which knows only a verified phone number.
 *
 * Read from the access token rather than fetched: this runs on the first screen
 * after sign-in, the claims are already in hand, and a round trip to learn
 * something the token may not contain would delay the form either way.
 */
function nameFromToken() {
  const claims = parseJwt(getToken()) || {};
  return (
    [claims.given_name, claims.family_name].filter(Boolean).join(' ')
    || claims.name
    || ''
  ).trim();
}

/**
 * An ASCII number quoted inside a Persian sentence, isolated so it reads
 * left-to-right with no CSS: U+2068 FSI … U+2069 PDI. The same wrap the kit puts
 * around the example in its own default phone copy — replacing that copy must
 * not lose it.
 */
const isolate = (value) => `\u2068${value}\u2069`;

const COMPLETE_STEP1 = `
  mutation CompleteOnboardingStep1($input: OnboardingStep1Input!) {
    completeOnboardingStep1(input: $input) {
      id fullName medicalCode specialty isDentist isOnboarded
    }
  }
`;

// JOIN_CLINIC is IMPORTED, not re-declared: the profile screen joins a clinic
// with the same mutation and the same selection set, and two copies of one
// document is how a field added for one screen goes missing on the other.

const CREATE_MY_CLINIC = `
  mutation CreateMyClinic($input: CreateOwnClinicInput!) {
    createMyClinic(input: $input) {
      id status clinic { id name }
    }
  }
`;

const STAGES = ['step1', 'clinic', 'done'];

/** One of the two answers to "own practice or existing clinic?".
 *
 *  A <button>, not a clickable <div>: this is the primary control of its screen,
 *  and it has to be reachable by keyboard and announced as an action. The whole
 *  card is the target rather than a link inside it — on a phone the card IS the
 *  tap area. */
const ChoiceCard = ({ title, body, onClick }) => (
  <button
    type="button"
    onClick={onClick}
    style={{
      display: 'block',
      width: '100%',
      textAlign: 'start',
      border: `1.5px solid ${C.border}`,
      borderRadius: 12,
      padding: '14px 16px',
      background: C.surface || '#fff',
      cursor: 'pointer',
      font: 'inherit',
    }}
  >
    <div style={{ fontSize: 13.5, fontWeight: 800, color: C.textPrimary, marginBottom: 4 }}>
      {title}
    </div>
    <div style={{ fontSize: 12, color: C.textSecondary, lineHeight: 1.8 }}>{body}</div>
  </button>
);

const Progress = ({ stage }) => {
  const { t } = useTranslation();
  const index = STAGES.indexOf(stage);
  const labels = [
    t('onboarding.progress.step1'),
    t('onboarding.progress.clinic'),
    t('onboarding.progress.done'),
  ];
  return (
    <div style={{ display: 'flex', gap: 8, marginBottom: 22 }}>
      {labels.map((label, i) => (
        <div key={label} style={{ flex: 1 }}>
          <div
            style={{
              height: 4,
              borderRadius: 3,
              background: i <= index ? C.teal : C.border,
              marginBottom: 6,
            }}
          />
          <div
            style={{
              fontSize: 11,
              fontWeight: i === index ? 800 : 500,
              color: i <= index ? C.textPrimary : C.textMuted,
            }}
          >
            {label}
          </div>
        </div>
      ))}
    </div>
  );
};

const OnboardingScreen = ({ onComplete }) => {
  const { t, locale } = useTranslation();
  const [stage, setStage] = React.useState('step1');
  const [busy, setBusy] = React.useState(false);
  const [error, setError] = React.useState(null);

  // Sign-in no longer knows the dentist's name.
  //
  // A phone + SMS-code account carries a verified phone number and no name
  // claims at all, and the name is printed on every prescription. So it is asked
  // for HERE, at the first moment it is needed, rather than by a signup form
  // nobody has a reason to fill in yet. An account registered before the change
  // still has the claims, so the field arrives pre-filled and reads as a
  // confirmation rather than a question.
  const [fullName, setFullName] = React.useState(() => nameFromToken());
  const [medicalCode, setMedicalCode] = React.useState('');
  const [bio, setBio] = React.useState('');
  const [code, setCode] = React.useState('');
  // Which branch of the clinic step is showing: the question, or one of its two
  // answers. Kept separate from `stage` so «بازگشت» returns to the question
  // without re-running step 1.
  const [clinicMode, setClinicMode] = React.useState('choose'); // choose | own | join
  const [clinicName, setClinicName] = React.useState('');
  const [clinicCity, setClinicCity] = React.useState('');
  const [clinicAddress, setClinicAddress] = React.useState('');
  // The practice line, in the two forms the field deals in: `clinicPhone` is
  // what gets SENT — E.164 and nothing else, or '' while the number is not yet a
  // number — and `clinicPhoneDraft` is what is in the box. Both are needed
  // because the canonical value is '' both when the dentist typed nothing and
  // when they typed something that is not a phone number, and those two must not
  // submit the same way: the second is a half-entered number we would otherwise
  // drop on the floor without ever saying so.
  const [clinicPhone, setClinicPhone] = React.useState('');
  const [clinicPhoneDraft, setClinicPhoneDraft] = React.useState('');
  // The currency the مطب keeps its books in — every tariff and طرح درمان is
  // counted in it. IRR unless the dentist says otherwise, which is what every
  // practice registered before this field was.
  const [clinicCurrency, setClinicCurrency] = React.useState(DEFAULT_CURRENCY);
  // The practice row exists but its currency never reached accounting. The BFF
  // cannot always find that row again on a retry (a clinic id is no longer the
  // account's own id), so pressing «ثبت مطب» again would register a SECOND
  // practice. Once this happens the button stays off and the message says why.
  const [setupIncomplete, setSetupIncomplete] = React.useState(false);
  const [joinedName, setJoinedName] = React.useState(null);

  const submitStep1 = async () => {
    if (!fullName.trim()) {
      setError(t('validation.fullName'));
      return;
    }
    if (!medicalCode.trim()) {
      setError(t('validation.medicalCode'));
      return;
    }
    setBusy(true);
    setError(null);
    try {
      await bffMutation(COMPLETE_STEP1, {
        input: {
          fullName: fullName.trim(),
          medicalCode: medicalCode.trim(),
          specialty: DENTAL_SPECIALTY,
          bio: bio.trim() || undefined,
        },
      });
      setStage('clinic');
    } catch (err) {
      setError(
        errorCode(err) === 'DUPLICATE_MEDICAL_CODE'
          ? t('profile.duplicateCode')
          : t('errors.generic'),
      );
    } finally {
      setBusy(false);
    }
  };

  const submitOwnClinic = async () => {
    // Enter in the name field reaches here too, not only the disabled button.
    if (setupIncomplete) return;
    if (!clinicName.trim()) {
      setError(t('validation.clinicName'));
      return;
    }
    // Optional, but not "optional to get right": something in the box that never
    // resolved would be silently discarded, and the dentist would find the field
    // empty later with no idea why.
    if (clinicPhoneDraft && !clinicPhone) {
      setError(t('validation.officePhone'));
      return;
    }
    setBusy(true);
    setError(null);
    try {
      const res = await bffMutation(CREATE_MY_CLINIC, {
        // OFFICE, the practice's currency, and the rest as typed — see
        // ownClinicInput for what each field is and why.
        input: ownClinicInput({
          name: clinicName,
          city: clinicCity,
          address: clinicAddress,
          officePhone: clinicPhone,
          currency: clinicCurrency,
        }),
      });
      setJoinedName(res?.createMyClinic?.clinic?.name || clinicName.trim());
      setStage('done');
    } catch (err) {
      // Say something true rather than "try again" — and, for a practice
      // created without its currency, stop a retry from making a second one.
      const failure = ownClinicFailure(err, { t, locale });
      if (failure.blocked) setSetupIncomplete(true);
      setError(failure.message);
    } finally {
      setBusy(false);
    }
  };

  const submitJoin = async () => {
    if (!code.trim()) {
      setError(t('validation.invitationCode'));
      return;
    }
    setBusy(true);
    setError(null);
    try {
      const res = await bffMutation(JOIN_CLINIC, { invitationCode: code.trim() });
      setJoinedName(res?.joinClinicByCode?.clinic?.name || null);
      setStage('done');
    } catch (err) {
      setError(
        errorCode(err) === 'CLINIC_NOT_FOUND'
          ? t('profile.clinics.notFound')
          : t('profile.clinics.joinFailed'),
      );
    } finally {
      setBusy(false);
    }
  };

  const subtitle = {
    step1: t('onboarding.subtitle.step1'),
    clinic: t('onboarding.subtitle.clinic'),
    done: t('onboarding.subtitle.done'),
  }[stage];

  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 100,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 24,
        overflowY: 'auto',
        background: `linear-gradient(145deg, ${C.navyDark} 0%, ${C.navy} 55%, ${C.navyLight} 100%)`,
        fontFamily: 'Meshkat, sans-serif',
      }}
    >
      <div
        style={{
          background: CT.surface,
          borderRadius: 18,
          width: 520,
          maxWidth: '100%',
          boxShadow: '0 32px 80px rgba(0,0,0,0.3)',
          overflow: 'hidden',
        }}
      >
        <div style={{ padding: '24px 28px 0', textAlign: 'center' }}>
          <img src={logo} alt="" style={{ width: 56, height: 56, objectFit: 'contain', marginBottom: 10 }} />
          <h1 style={{ fontSize: 19, fontWeight: 800, color: C.navy, margin: '0 0 6px' }}>
            {t('onboarding.title')}
          </h1>
          <p style={{ fontSize: 13, color: C.textSecondary, margin: '0 0 20px' }}>{subtitle}</p>
        </div>

        <div style={{ padding: '0 28px 26px' }}>
          <Progress stage={stage} />

          {/* role="alert": this line appears after a click, below a form the
              dentist is still looking at, and it is the only account of why
              nothing happened. */}
          {error && (
            <Alert variant="error" role="alert" style={{ marginBottom: 14 }}>
              {error}
            </Alert>
          )}

          {/* aria-label on every field: the kit's FormField renders its label as
              a <span> with no htmlFor/id, so the visible label is not attached
              to the control and these would be unnamed boxes on the very first
              screen a new dentist meets. */}
          {stage === 'step1' && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
              <TextInput
                label={t('onboarding.step1.fullName')}
                aria-label={t('onboarding.step1.fullName')}
                required
                aria-required="true"
                placeholder={t('onboarding.step1.fullNamePlaceholder')}
                value={fullName}
                onChange={(e) => setFullName(e.target.value)}
              />
              <TextInput
                label={t('onboarding.step1.medicalCode')}
                aria-label={t('onboarding.step1.medicalCode')}
                required
                aria-required="true"
                dir="ltr"
                placeholder={t('onboarding.step1.medicalCodePlaceholder')}
                value={medicalCode}
                // A one-field step: Enter continues it.
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && !busy) {
                    e.preventDefault();
                    submitStep1();
                  }
                }}
                onChange={(e) => setMedicalCode(e.target.value)}
              />
              {/* The LABEL is copy and follows the UI language; the VALUE sent
                  is always DENTAL_SPECIALTY (submitStep1 above), because that
                  is the identifier the server-side predicate matches on. */}
              <TextInput
                label={t('onboarding.step1.specialty')}
                aria-label={t('onboarding.step1.specialty')}
                value={t('profile.specialtyValue')}
                onChange={() => {}}
                disabled
                helperText={t('onboarding.step1.specialtyNote')}
              />
              <Textarea
                label={t('onboarding.step1.bio')}
                aria-label={t('onboarding.step1.bio')}
                dir="auto"
                rows={3}
                value={bio}
                onChange={(e) => setBio(e.target.value)}
              />
              <Button onClick={submitStep1} disabled={busy} className="w-full">
                {busy ? t('common.actions.saving') : t('onboarding.step1.submit')}
              </Button>
            </div>
          )}

          {stage === 'clinic' && clinicMode === 'choose' && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
              <h2 style={{ margin: 0, fontSize: 14, fontWeight: 800, color: C.textPrimary }}>
                {t('onboarding.clinic.choiceTitle')}
              </h2>
              <div style={{ fontSize: 12.5, color: C.textSecondary, lineHeight: 1.9 }}>
                {t('onboarding.clinic.choiceDescription')}
              </div>

              {/* Two routes, ranked but not hidden. Owning a practice and joining
                  someone else's are different situations, not a preference — so
                  they are offered as a question rather than one path with the
                  other tucked behind a link. */}
              <ChoiceCard
                title={t('onboarding.clinic.ownTitle')}
                body={t('onboarding.clinic.ownBody')}
                onClick={() => { setError(null); setClinicMode('own'); }}
              />
              <ChoiceCard
                title={t('onboarding.clinic.joinTitle')}
                body={t('onboarding.clinic.joinBody')}
                onClick={() => { setError(null); setClinicMode('join'); }}
              />

              {/* Skipping is a real choice, not an escape hatch: امروز works
                  without a clinic (the day is scoped to the dentist), only the
                  patient register needs one — and بیماران says so. */}
              <Button variant="outline" onClick={() => setStage('done')} disabled={busy}>
                {t('onboarding.clinic.skip')}
              </Button>
            </div>
          )}

          {stage === 'clinic' && clinicMode === 'own' && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
              <h2 style={{ margin: 0, fontSize: 14, fontWeight: 800, color: C.textPrimary }}>
                {t('onboarding.clinic.ownTitle')}
              </h2>
              <TextInput
                label={t('onboarding.clinic.ownName')}
                aria-label={t('onboarding.clinic.ownName')}
                required
                aria-required="true"
                placeholder={t('onboarding.clinic.ownNamePlaceholder')}
                value={clinicName}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && !busy) { e.preventDefault(); submitOwnClinic(); }
                }}
                onChange={(e) => setClinicName(e.target.value)}
              />
              <TextInput
                label={t('onboarding.clinic.ownCity')}
                aria-label={t('onboarding.clinic.ownCity')}
                value={clinicCity}
                onChange={(e) => setClinicCity(e.target.value)}
              />
              <TextInput
                label={t('onboarding.clinic.ownAddress')}
                aria-label={t('onboarding.clinic.ownAddress')}
                value={clinicAddress}
                onChange={(e) => setClinicAddress(e.target.value)}
              />
              <Select
                label={t('onboarding.clinic.currency')}
                aria-label={t('onboarding.clinic.currency')}
                helperText={t('onboarding.clinic.currencyHint')}
                placeholder={null}
                value={clinicCurrency}
                onChange={(e) => setClinicCurrency(e.target.value)}
                options={currencyOptions({ t, locale })}
                data-testid="onboarding-clinic-currency"
              />
              <PhoneInput
                label={t('onboarding.clinic.ownPhone')}
                aria-label={t('onboarding.clinic.ownPhone')}
                countryLabel={t('common.labels.country')}
                // A practice's number is an OFFICE LINE: «۰۲۱۸۸…» is the
                // ordinary answer here, and the mobile rule would refuse it.
                allowLandline
                // The kit's copy is Persian by default (DESIGN.md rule 7) and
                // this app ships `en` too. Only these three are reachable: the
                // field is optional, so `required` never fires, and `notMobile`
                // cannot fire with `allowLandline` on.
                messages={{
                  tooShort: (ctx) => t('validation.phone.tooShort', {
                    country: ctx.countryName, example: isolate(ctx.example),
                  }),
                  tooLong: (ctx) => t('validation.phone.tooLong', {
                    country: ctx.countryName, example: isolate(ctx.example),
                  }),
                  invalid: t('validation.phone.invalid'),
                }}
                value={clinicPhone}
                // A VALUE, not an event: the canonical number first, the rest of
                // the parse second.
                onChange={(e164, meta) => {
                  setClinicPhone(e164);
                  setClinicPhoneDraft(meta?.draft ?? '');
                }}
              />
              <div style={{ display: 'flex', gap: 10 }}>
                <Button onClick={submitOwnClinic} disabled={busy || setupIncomplete} className="flex-1">
                  {busy ? t('common.actions.saving') : t('onboarding.clinic.create')}
                </Button>
                <Button variant="outline" onClick={() => { setError(null); setClinicMode('choose'); }} disabled={busy}>
                  {t('onboarding.clinic.back')}
                </Button>
              </div>
            </div>
          )}

          {stage === 'clinic' && clinicMode === 'join' && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
              <h2 style={{ margin: 0, fontSize: 14, fontWeight: 800, color: C.textPrimary }}>
                {t('onboarding.clinic.title')}
              </h2>
              <div style={{ fontSize: 12.5, color: C.textSecondary, lineHeight: 1.9 }}>
                {t('onboarding.clinic.description')}
              </div>
              <TextInput
                label={t('onboarding.clinic.placeholder')}
                aria-label={t('onboarding.clinic.placeholder')}
                dir="ltr"
                value={code}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && !busy) { e.preventDefault(); submitJoin(); }
                }}
                onChange={(e) => setCode(e.target.value)}
              />
              <div style={{ display: 'flex', gap: 10 }}>
                <Button onClick={submitJoin} disabled={busy} className="flex-1">
                  {busy ? t('common.actions.saving') : t('onboarding.clinic.join')}
                </Button>
                <Button variant="outline" onClick={() => { setError(null); setClinicMode('choose'); }} disabled={busy}>
                  {t('onboarding.clinic.back')}
                </Button>
              </div>
            </div>
          )}

          {stage === 'done' && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 14, textAlign: 'center' }}>
              <h2 style={{ margin: 0, fontSize: 16, fontWeight: 800, color: C.textPrimary }}>
                {t('onboarding.done.title')}
              </h2>
              <div style={{ fontSize: 13, color: C.textSecondary, lineHeight: 1.9 }}>
                {t('onboarding.done.body')}
              </div>
              {joinedName && (
                <div
                  style={{
                    background: CT.goodSoft,
                    color: CT.goodInk,
                    borderRadius: 10,
                    padding: '10px 12px',
                    fontSize: 13,
                  }}
                >
                  {t('profile.clinics.joined', { name: joinedName })}
                </div>
              )}
              <Button onClick={onComplete} className="w-full">
                {t('onboarding.done.enter')}
              </Button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default OnboardingScreen;
